
import axios from 'axios';

/**
 * Obtener categorías de recargo desde el backend
 * @returns {Promise} - Lista de categorías
 */
export const getCategoriasRecargo = async () => {
  const mainUrl = `${API_BASE}/financiero/categorias/`;
  const altUrls = [
    `${API_BASE}/financiero/categorias`,
    `${API_BASE}/financiero/categorias/list/`,
    `${API_BASE}/financiero/categorias/list`
  ];

  // Intentar varias variantes del endpoint y devolver array vacío en caso de fallo
  try {
    let lastErr = null;
    const tryUrls = [mainUrl, ...altUrls];
    for (const url of tryUrls) {
      try {
        const response = await axios.get(url);
        // Normalizar distintas formas de respuesta
        const data = response && response.data ? response.data : [];
        return data;
      } catch (err) {
        lastErr = err;
        // continuar con la siguiente variante
      }
    }
    console.error('Error al obtener categorías de recargo (todas las variantes fallaron):', lastErr);
    // Intentar usar 'billing-concepts' como fuente alternativa de categorías
    try {
      const bcUrl = `${API_BASE}/financiero/billing-concepts/`;
      const resp = await axios.get(bcUrl);
      const list = resp && resp.data ? (Array.isArray(resp.data) ? resp.data : (resp.data.results || [])) : [];
      // Extraer categorías únicas encontradas en los conceptos
      const seen = new Set();
      const cats = [];
      for (const item of list) {
        const catName = (item.categoria || item.nombre_categoria || item.subcategoria || item.categoria_concepto || '').trim();
        if (!catName) continue;
        const key = catName.toUpperCase();
        if (!seen.has(key)) {
          seen.add(key);
          cats.push({ categoria: catName, nombre_categoria: catName, monto_cargo: null, periodicidad: null, dia_limite: null });
        }
      }
      if (cats.length > 0) return cats;
    } catch (err) {
      // ignore and fallthrough
    }
    return [];
  } catch (error) {
    console.error('Error inesperado al obtener categorías de recargo:', error);
    return [];
  }
};

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000/api';
const API_URL = `${API_BASE}/financiero/cargos-manuales`;

// Permitir envío de cookies (si el backend usa sesión) en peticiones cross-site
axios.defaults.withCredentials = true;

// Mapeo local (frontend) de reglas de recargo por categoría.
// Si el backend no expone un endpoint de categorías, use estas reglas.
// Edítelas según las políticas de su institución.
export const CATEGORIA_REGLAS = {
  // Ejemplo: 'PARCIALIDADES': { dia_limite: 5, periodicidad: 'MENSUAL', monto_cargo: 10 }
};

// Configurar axios para incluir el token de autenticación
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Obtener estudiantes de un campus específico
 * @param {number} campusId - ID del campus
 * @param {string} search - Término de búsqueda opcional (nombre o matrícula)
 * @returns {Promise} - Lista de estudiantes
 */
export const getEstudiantesCampus = async (campusId, search = '', page = 1, page_size = 8) => {
  try {
    let url = `${API_URL}/${campusId}/estudiantes/`;
    const parts = [];
    if (search && search.trim() !== '') parts.push(`search=${encodeURIComponent(search.trim())}`);
    if (page) parts.push(`page=${encodeURIComponent(page)}`);
    if (page_size) parts.push(`page_size=${encodeURIComponent(page_size)}`);
    if (parts.length) url += `?${parts.join('&')}`;
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error('Error al obtener estudiantes:', error);
    throw error;
  }
};

/**
 * Obtener conceptos de cobro activos
 * @param {object} filters - Filtros opcionales { campus, categoria }
 * @returns {Promise} - Lista de conceptos
 */
export const getConceptosActivos = async (filters = {}) => {
  try {
    let url = `${API_URL}/conceptos/`;
    const queryParams = [];
    
    if (filters.campus) {
      queryParams.push(`campus=${filters.campus}`);
    }
    
    if (filters.categoria) {
      queryParams.push(`categoria=${encodeURIComponent(filters.categoria)}`);
    }
    
    if (filters.seccion) {
      queryParams.push(`seccion=${encodeURIComponent(filters.seccion)}`);
    }
    
    if (filters.ciclo_escolar) {
      queryParams.push(`ciclo_escolar=${encodeURIComponent(filters.ciclo_escolar)}`);
    }
    
    if (queryParams.length > 0) {
      url += `?${queryParams.join('&')}`;
    }
    
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error('Error al obtener conceptos:', error);
    throw error;
  }
};

/**
 * Crear uno o múltiples cargos manuales
 * @param {Array|Object} cargos - Array de cargos o un solo cargo
 * @returns {Promise} - Respuesta del servidor
 * 
 * Formato de cada cargo:
 * {
 *   id_estudiante: number,
 *   id_concepto: number,
 *   id_ciclo_escolar: number,
 *   monto: number,
 *   cantidad: number,
 *   descuento: number (0-100),
 *   fecha_aplicacion: string (YYYY-MM-DD),
 *   fecha_vencimiento: string (YYYY-MM-DD),
 *   motivo: string (opcional)
 * }
 */
export const crearCargosManuales = async (cargos) => {
  try {
    const url = `${API_URL}/crear/`;
    
    // Si es un array, enviarlo con la clave "cargos"
    const payload = Array.isArray(cargos) ? { cargos } : cargos;
    
    const response = await axios.post(url, payload);
    return response.data;
  } catch (error) {
    console.error('Error al crear cargos:', error);
    
    // Extraer mensaje de error del servidor
    if (error.response && error.response.data) {
      throw {
        message: error.response.data.error || 'Error al crear cargos',
        details: error.response.data
      };
    }
    
    throw error;
  }
};

/**
 * Obtener campus disponibles (reutiliza endpoint existente)
 * @returns {Promise} - Lista de campus
 */
export const getCampusDropdown = async () => {
  try {
    const response = await axios.get(`${API_BASE}/financiero/campus-dropdown/`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener campus:', error);
    throw error;
  }
};

/**
 * Obtener ciclos escolares disponibles (reutiliza endpoint existente)
 * @param {object} options - Filtros opcionales (por ejemplo { vigente: true })
 * @returns {Promise} - Lista de ciclos
 */
export const getCiclosEscolares = async (options = {}) => {
  try {
    const params = new URLSearchParams();

    if (options.vigente) {
      params.append('vigente', 'true');
    }

    const queryString = params.toString();
    const url = `${API_BASE}/financiero/ciclos-dropdown/${queryString ? `?${queryString}` : ''}`;

    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error('Error al obtener ciclos:', error);
    throw error;
  }
};

// Funciones auxiliares para el frontend

/**
 * Calcular el monto neto de un cargo
 * @param {number} monto - Monto unitario
 * @param {number} cantidad - Cantidad
 * @param {number} descuento - Porcentaje de descuento (0-100)
 * @returns {number} - Monto neto
 */
export const calcularMontoNeto = (monto, cantidad, descuento) => {
  const montoOriginal = parseFloat(monto) * parseInt(cantidad);
  const montoDescuento = (montoOriginal * parseFloat(descuento)) / 100;
  return montoOriginal - montoDescuento;
};

/**
 * Validar datos de un cargo antes de enviar
 * @param {object} cargo - Objeto con datos del cargo
 * @returns {object} - { valid: boolean, errors: array }
 */
export const validarCargo = (cargo) => {
  const errors = [];
  
  if (!cargo.id_estudiante) {
    errors.push('Debe seleccionar un estudiante');
  }
  
  if (!cargo.id_concepto) {
    errors.push('Debe seleccionar un concepto de cobro');
  }
  
  if (!cargo.id_ciclo_escolar) {
    errors.push('Debe seleccionar un ciclo escolar');
  }
  
  if (!cargo.monto || parseFloat(cargo.monto) <= 0) {
    errors.push('El monto debe ser mayor a cero');
  }
  
  if (!cargo.cantidad || parseInt(cargo.cantidad) <= 0) {
    errors.push('La cantidad debe ser mayor a cero');
  }
  
  if (cargo.descuento !== undefined && (parseFloat(cargo.descuento) < 0 || parseFloat(cargo.descuento) > 100)) {
    errors.push('El descuento debe estar entre 0 y 100');
  }
  
  if (!cargo.fecha_aplicacion) {
    errors.push('Debe especificar la fecha de aplicación');
  }
  
  if (!cargo.fecha_vencimiento) {
    errors.push('Debe especificar la fecha de vencimiento');
  }
  
  if (cargo.fecha_aplicacion && cargo.fecha_vencimiento) {
    const fechaAplicacion = new Date(cargo.fecha_aplicacion);
    const fechaVencimiento = new Date(cargo.fecha_vencimiento);
    
    if (fechaVencimiento < fechaAplicacion) {
      errors.push('La fecha de vencimiento no puede ser anterior a la fecha de aplicación');
    }
  }
  
  return {
    valid: errors.length === 0,
    errors
  };
};

/**
 * Obtener adeudos pendientes de un estudiante
 * @param {number} estudianteId - ID del estudiante
 * @returns {Promise} - Datos de adeudos pendientes
 */
export const getAdeudosPendientes = async (estudianteId) => {
  try {
    const response = await axios.get(`${API_URL}/estudiante/${estudianteId}/adeudos/`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener adeudos pendientes:', error);
    throw error;
  }
};

/**
 * Obtener categoría por nombre (nombre_categoria)
 * @param {string} nombre - Nombre de la categoría
 * @returns {Promise<object|null>} - Objeto de categoría o null
 */
export const getCategoriaByNombre = async (nombre) => {
  try {
    if (!nombre) return null;
    // Revisión rápida en el mapeo local de reglas
    const key = String(nombre).trim();
    if (CATEGORIA_REGLAS && CATEGORIA_REGLAS[key]) {
      const r = CATEGORIA_REGLAS[key];
      return {
        nombre_categoria: key,
        dia_limite: r.dia_limite,
        periodicidad: r.periodicidad,
        monto_cargo: r.monto_cargo
      };
    }
    const url = `${API_BASE}/financiero/categorias/`;
    // Intentar varias formas de consulta según cómo esté definido el backend
    const tryParams = [
      { nombre: nombre },
      { nombre_categoria: nombre },
      { search: nombre },
      { q: nombre },
      { categoria: nombre }
    ];

    for (const params of tryParams) {
      try {
        const response = await axios.get(url, { params });
        if (response && response.data) {
          if (Array.isArray(response.data) && response.data.length > 0) return response.data[0];
          // Si devuelve objeto único
          if (typeof response.data === 'object') return response.data;
        }
      } catch (err) {
        // Ignorar y probar siguiente variante
        // Si es 404 o similar, continuamos con la siguiente opción
      }
    }

    // Fallback: obtener la lista completa y filtrar localmente (por si el endpoint no permite buscar por query)
    try {
      const allResp = await axios.get(url);
      const list = allResp && allResp.data ? (Array.isArray(allResp.data) ? allResp.data : (allResp.data.results || [])) : [];
      if (Array.isArray(list) && list.length > 0) {
        const lower = String(nombre).trim().toLowerCase();
        const found = list.find((c) => {
          if (!c) return false;
          return [c.nombre, c.nombre_categoria, c.name, c.nombreCategoria, c.descripcion]
            .filter(Boolean)
            .some((v) => String(v).toLowerCase().includes(lower));
        });
        return found || null;
      }
    } catch (err) {
      // No hacer nada más
    }

    // Último recurso: si no existe el endpoint de 'categorias', intentar obtener categorías desde 'billing-concepts'
    try {
      const bcUrl = `${API_BASE}/financiero/billing-concepts/`;
      const resp = await axios.get(bcUrl, { params: { search: nombre } });
      const bcList = resp && resp.data ? (Array.isArray(resp.data) ? resp.data : (resp.data.results || [])) : [];
      if (Array.isArray(bcList) && bcList.length > 0) {
        // Buscar una categoría coincidente dentro de los conceptos
        const lower = String(nombre).trim().toLowerCase();
        for (const item of bcList) {
          const cand = [item.categoria, item.nombre_concepto, item.nombre_concepto, item.subcategoria]
            .filter(Boolean)
            .map(v => String(v).toLowerCase());
          if (cand.some(v => v.includes(lower))) {
            // Construir un objeto compatible con la forma esperada por el frontend
            return {
              nombre_categoria: item.categoria || item.nombre_concepto,
              categoria: item.categoria || item.nombre_concepto,
              dia_limite: null,
              periodicidad: null,
              monto_cargo: null
            };
          }
        }
      }
    } catch (err) {
      // ignorar
    }

    return null;
  } catch (error) {
    console.error('Error al obtener categoría por nombre:', error);
    return null;
  }
};

/**
 * Obtener configuración de recibos (como correos en copia definidos en el backend)
 * @returns {Promise<{ cc_emails: string[] }>}
 */
export const getConfigRecibos = async () => {
  try {
    // Ruta corregida: /api/financiero/config/recibos/
    const response = await axios.get(`${API_BASE}/financiero/config/recibos/`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener configuración de recibos:', error);
    throw error;
  }
};

/**
 * Enviar por correo el recibo de cargos manuales
 * @param {object} params
 * @param {string} params.toEmail - Correo del destinatario (estudiante)
 * @param {string} params.pdfBase64 - PDF en base64 (puede incluir prefijo data:application/pdf;base64,)
 * @param {string} [params.subject] - Asunto opcional del correo
 * @param {string} [params.paymentUrl] - (Opcional) Liga de pago en línea
 * @returns {Promise} - Respuesta del servidor
 */
export const enviarReciboCargosManuales = async ({ toEmail, pdfBase64, subject, paymentUrl }) => {
  try {
    const url = `${API_URL}/enviar-recibo/`;

    const payload = {
      to_email: toEmail,
      pdf_base64: pdfBase64,
    };

    if (subject) {
      payload.subject = subject;
    }

    if (paymentUrl) {
      payload.payment_url = paymentUrl;
    }

    const response = await axios.post(url, payload);
    return response.data;
  } catch (error) {
    console.error('Error al enviar el recibo por correo:', error);

    if (error.response && error.response.data) {
      throw {
        message: error.response.data.error || 'Error al enviar el recibo por correo',
        details: error.response.data,
      };
    }

    throw error;
  }
};

/**
 * Eliminar un cargo pendiente
 * @param {number} cargoId - ID del cargo a eliminar
 * @returns {Promise} - Respuesta del servidor
 */
export const eliminarCargo = async (cargoId) => {
  try {
    const response = await axios.delete(`${API_URL}/cargo/${cargoId}/eliminar/`);
    return response.data;
  } catch (error) {
    console.error('Error al eliminar cargo:', error);
    
    if (error.response && error.response.data) {
      throw {
        message: error.response.data.error || 'Error al eliminar el cargo',
        details: error.response.data
      };
    }
    
    throw error;
  }
};

/**
 * Formatear fecha para mostrar en interfaz
 * @param {string} fecha - Fecha en formato YYYY-MM-DD
 * @returns {string} - Fecha en formato DD/MM/YYYY
 */
export const formatearFecha = (fecha) => {
  if (!fecha) return '-';
  try {
    const [year, month, day] = fecha.split('-');
    return `${day}/${month}/${year}`;
  } catch {
    return fecha;
  }
};

/**
 * Formatear monto para mostrar en interfaz
 * @param {number} monto - Monto numérico
 * @returns {string} - Monto formateado con separador de miles y 2 decimales
 */
export const formatearMonto = (monto) => {
  if (!monto && monto !== 0) return '$0.00';
  return `$${parseFloat(monto).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/**
 * Obtener información de beca del estudiante
 * @param {number} estudianteId - ID del estudiante
 * @returns {Promise} - Datos de beca del estudiante
 *  {
 *    success: bool,
 *    tiene_beca: bool,
 *    beca_activa: { nombre_beca, porcentaje_otorgado, tipo_beca, estado } or null,
 *    historial_becas: []
 *  }
 */
export const getBecaEstudiante = async (estudianteId) => {
  try {
    const response = await axios.get(`${API_BASE}/financiero/estudiante/${estudianteId}/beca/`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener beca del estudiante:', error);
    throw error;
  }
};

/**
 * Recalcular los descuentos por becas en los cargos de un estudiante
 * @param {number} estudianteId - ID del estudiante
 * @returns {Promise} - Respuesta del servidor
 */
export const recalcularBecaCargos = async (estudianteId) => {
  try {
    const response = await axios.post(`${API_BASE}/financiero/estudiante/${estudianteId}/recalcular-beca/`);
    return response.data;
  } catch (error) {
    console.error('Error al recalcular descuentos de beca:', error);
    if (error.response && error.response.data) {
      throw {
        message: error.response.data.error || 'Error al recalcular descuentos de beca',
        details: error.response.data
      };
    }
    throw error;
  }
};
